/**
 * Shared utility functions for AnalysisPanel section components.
 * Color helpers, icon helpers, and label converters.
 */

import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import type { ReactElement } from 'react';
import { createElement } from 'react';

// ---------------------------------------------------------------------------
// Score → color helpers
// ---------------------------------------------------------------------------

export function getScoreColor(score: number): string {
  if (score >= 80) return 'text-success';
  if (score >= 60) return 'text-info';
  if (score >= 40) return 'text-warning';
  return 'text-danger';
}

export function getProgressColor(score: number): string {
  if (score >= 80) return 'bg-success';
  if (score >= 60) return 'bg-info';
  if (score >= 40) return 'bg-warning';
  return 'bg-danger';
}

export function getPriorityColor(priority?: string): string {
  if (priority === 'high') return 'text-danger';
  if (priority === 'medium') return 'text-warning';
  return 'text-info';
}

// ---------------------------------------------------------------------------
// Importance icon (key moments)
// ---------------------------------------------------------------------------

export function getImportanceIcon(importance?: string): string {
  if (importance === 'high') return '🔴';
  if (importance === 'medium') return '🟡';
  return '🟢';
}

// ---------------------------------------------------------------------------
// Sentiment helpers
// ---------------------------------------------------------------------------

export function getSentimentIcon(delta: number): ReactElement {
  if (delta > 0.2)
    return createElement(TrendingUp, { className: 'h-4 w-4 text-success' });
  if (delta < -0.2)
    return createElement(TrendingDown, { className: 'h-4 w-4 text-danger' });
  return createElement(BarChart3, { className: 'h-4 w-4 text-fg-subtle' });
}

/**
 * Convert sentiment value (-1 to +1) to human-readable label.
 */
export function getSentimentLabel(value: number | undefined): string {
  if (value === undefined || value === null) return '?';
  if (value >= 0.5) return 'Дуже позитивний';
  if (value >= 0.2) return 'Позитивний';
  if (value >= -0.2) return 'Нейтральний';
  if (value >= -0.5) return 'Негативний';
  return 'Дуже негативний';
}

export function getSentimentColor(value: number | undefined): string {
  if (value === undefined || value === null) return 'text-fg-subtle';
  if (value >= 0.5) return 'text-success';
  if (value >= 0.2) return 'text-success/80';
  if (value >= -0.2) return 'text-fg-muted';
  if (value >= -0.5) return 'text-warning';
  return 'text-danger';
}

export function getSentimentBgColor(value: number | undefined): string {
  if (value === undefined || value === null) return 'bg-bg-muted';
  if (value >= 0.5) return 'bg-success/20';
  if (value >= 0.2) return 'bg-success/10';
  if (value >= -0.2) return 'bg-bg-surface';
  if (value >= -0.5) return 'bg-warning/20';
  return 'bg-danger/20';
}

// ---------------------------------------------------------------------------
// A-F component score metadata
// ---------------------------------------------------------------------------

/** Progress-bar colors for A-F component score keys */
export const COMPONENT_SCORE_COLORS: Record<string, string> = {
  greetingScore: 'bg-primary',
  needsScore: 'bg-success',
  objectionScore: 'bg-warning',
  closingScore: 'bg-info',
  politenessScore: 'bg-primary',
  clarityScore: 'bg-danger',
};

// ---------------------------------------------------------------------------
// Quality marker keys (agent review)
// ---------------------------------------------------------------------------

export const QUALITY_MARKER_KEYS = [
  { id: 'missing_discovery', labelKey: 'records.agentReview.markers.missingDiscovery' },
  {
    id: 'weak_value_proposition',
    labelKey: 'records.agentReview.markers.weakValueProposition',
  },
  {
    id: 'poor_objection_handling',
    labelKey: 'records.agentReview.markers.poorObjectionHandling',
  },
  { id: 'tone_issues', labelKey: 'records.agentReview.markers.toneIssues' },
  {
    id: 'incomplete_follow_up',
    labelKey: 'records.agentReview.markers.incompleteFollowUp',
  },
] as const;
